import { Controller, useForm } from "react-hook-form";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormHelperText,
  InputLabel,
} from "@mui/material";
import {
  BodyMedium,
  H6HeadlineSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  StyledIconButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  StyledFormControl,
  StyledOutlinedInput,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import { useAuth0 } from "@auth0/auth0-react";
import { usePostNewTag } from "../../queries/usePostAddTag.ts";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { set } from "lodash";

const TagFormHeader = ({
  setOpen,
  formReset,
  setActiveStep,
}) => {
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0"}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
      >
        <H6HeadlineSmall>{"Add a tag"}</H6HeadlineSmall>

        <BodyMedium>
          Enter a tag you would like to add.
        </BodyMedium>
      </Box>

      <StyledIconButton 
        colors={colors}
        onClick={() => {
          setOpen(false);
          formReset();
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const TagNamingForm = ({ setOpen, refetch }) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      tagName: "",
    },
  });
  const { colors } = useTheme();
  const watchTagName = watch("tagName");
  const [authToken, setAuthToken] = useState("");
  const { getAccessTokenSilently } = useAuth0();
  const { mutate, isPending } = usePostNewTag();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();

  const onSubmit = async (data) => {
    console.log("Form data:", data);
    const { tagName } = data;
    mutate(tagName, {
      onSuccess: async (response) => {
        if (response.status === "ok") {
          setToastMessage("Tag added successfully");
          setToastOpen(true);
          await refetch();
          setOpen(false);
          reset();
        } else {
          setToastMessage(
            "Error: adding the tag: " +
              response.msg
          );
          setToastOpen(true);
          setOpen(false);
          reset();
        }
      },
      onError: (error) => {
        console.error("Error adding tag:", error);
        setToastMessage(
          "Error: adding the tag: " + error
        );
        setToastOpen(true);
        setOpen(false);
        reset();
      },
    });
  };

  React.useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  return (
    <>
      <TagFormHeader setOpen={setOpen} formReset={reset} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={6}
        >
          <StyledFormControl
            colors={colors}
            fullWidth
            margin="none"
            error={!!errors.tagName}
          >
            <InputLabel htmlFor="tag-name">Name</InputLabel>
            <Controller
              name="tagName"
              control={control}
              defaultValue={""}
              rules={{
                required: "Tag name is required",
                maxLength: {
                  value: 30,
                  message:
                    "Tag name cannot exceed 30 characters",
                },
                pattern: {
                  value: /^[a-zA-Z0-9._-]+$/,
                  message:
                    "Tag name can only contain letters, numbers, dots, dashes, and underscores",
                },
              }}
              render={({ field }) => (
                <StyledOutlinedInput
                  colors={colors}
                  {...field}
                  label="Name"
                  id="tag-name-input"
                  style={{
                    flex: 1,
                    margin: 0,
                    minHeight: "2.5rem",
                    // padding: "16.5px 14px",
                  }}
                  error={!!errors.tagName}
                  helperText={
                    errors.tagName
                      ? errors.tagName.message
                      : ""
                  }
                  value={watchTagName}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("tagName", e.target.value);
                  }}
                />
              )}
            />
            {errors.tagName && (
              <FormHelperText>
                {errors.tagName.message}
              </FormHelperText>
            )}
          </StyledFormControl>

          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            flexGrow={1}
          >
            <DialogActions>
              <PrimaryButton colors={colors} type="submit">
                Submit
              </PrimaryButton>
            </DialogActions>
          </Box>
        </Box>
      </form>
    </>
  );
};

const AddTagsDialog = ({ open, setOpen, refetch }) => {
  const { colors } = useTheme();
  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={open}
      hideBackdrop
    >
      <DialogContent sx={{ padding: 0 }}>
        <TagNamingForm
          setOpen={setOpen}
          refetch={refetch}
        />
      </DialogContent>
    </StyledDialog>
  );
};

export default AddTagsDialog;
